import { z } from "zod";
import { optionalUnlessRequired, type FieldDescriptor } from "./types.js";
import { optionSchema } from "./choiceCommon.js";

const configSchema = z.object({
  options: z.array(optionSchema).default([]),
  minSelections: z.number().int().nonnegative().optional(),
  maxSelections: z.number().int().positive().optional(),
});

export const multiSelectField: FieldDescriptor<z.infer<typeof configSchema>> = {
  type: "multi_select",
  label: "Multiple choice",
  category: "choice",
  icon: "ListChecks",
  configSchema,
  /**
   * One `superRefine` rather than chained `.refine()` calls: each `.refine()` returns a
   * `ZodEffects`, so a later `.min()` is no longer available on it — chaining array methods after
   * a refinement silently requires a cast, and that cast is a lie that throws at runtime.
   */
  valueSchema: (config, isRequired) => {
    const allowed = config.options.map((option) => option.value);
    const effectiveMin = isRequired ? Math.max(config.minSelections ?? 1, 1) : config.minSelections;

    const schema = z.array(z.string()).superRefine((values, ctx) => {
      if (allowed.length > 0) {
        for (const value of values) {
          if (!allowed.includes(value)) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: "One or more selections are not valid options",
            });
            break;
          }
        }
      }

      // An optional field may be left empty entirely; if answered, it still must meet its minimum.
      const skipMinimum = !isRequired && values.length === 0;
      if (effectiveMin !== undefined && !skipMinimum && values.length < effectiveMin) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: `Select at least ${effectiveMin}` });
      }

      if (config.maxSelections !== undefined && values.length > config.maxSelections) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `Select no more than ${config.maxSelections}`,
        });
      }
    });

    return optionalUnlessRequired(schema, isRequired);
  },
  defaultConfig: { options: [] },
  supportsProfileBinding: false,
  defaultSearchable: true,
  renderHints: {},
};
